import { PhotoSwipeLightbox } from 'vendor';

/**
 * Öffnet jedes Bild innerhalb des Elements per Klick in der Prestige-Lightbox (PhotoSwipe).
 * Die Bedienelemente entsprechen denen der Prestige-Produktgalerie.
 */
class DbdLightbox extends HTMLElement {
  #lightbox;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#lightbox?.destroy();
    this.#lightbox = null;
  }

  get images() {
    return Array.from(this.querySelectorAll('img'));
  }

  // Wird erst beim ersten Öffnen erzeugt, damit beim Laden der Seite nichts passiert
  get lightbox() {
    if (this.#lightbox) {
      return this.#lightbox;
    }

    this.#lightbox = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      maxZoomLevel: 3,
      closeTitle: window.themeVariables.strings.closeGallery,
      zoomTitle: window.themeVariables.strings.zoomGallery,
      errorMsg: window.themeVariables.strings.errorGallery,
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      close: false,
      zoom: false
    });

    this.#lightbox.on('uiRegister', () => this.#registerUi());
    this.#lightbox.addFilter('thumbEl', (thumbEl, data) => data.thumbnailElement);

    // Nach dem Schließen zeigt eine Galerie das zuletzt angesehene Bild
    this.#lightbox.on('close', () => {
      this.querySelector('scroll-carousel')?.select(this.#lightbox.pswp.currIndex, { instant: true });
    });

    this.#lightbox.init();
    return this.#lightbox;
  }

  #onClick = (event) => {
    // defaultPrevented: scroll-carousel unterdrückt den Klick nach einem Maus-Drag
    if (event.defaultPrevented || event.target.tagName !== 'IMG') {
      return;
    }

    const images = this.images;

    this.lightbox.loadAndOpen(images.indexOf(event.target), images.map((image) => ({
      thumbnailElement: image,
      src: image.src,
      srcset: image.srcset,
      msrc: image.currentSrc || image.src,
      width: parseInt(image.getAttribute('width')),
      height: parseInt(image.getAttribute('height')),
      alt: image.alt,
      thumbCropped: true
    })));
  };

  #registerUi() {
    const pswp = this.#lightbox.pswp;

    pswp.ui.registerElement({
      name: 'close-button',
      className: 'circle-button circle-button--xl hover:animate-icon-block',
      ariaLabel: window.themeVariables.strings.closeGallery,
      order: 2,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
        </svg>
      `,
      onClick: () => pswp.close()
    });

    if (pswp.options.dataSource.length > 1) {
      pswp.ui.registerElement({
        name: 'previous-button',
        className: 'circle-button hover:animate-icon-inline',
        ariaLabel: window.themeVariables.strings.previous,
        order: 1,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
            <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
          </svg>
        `,
        onClick: () => pswp.prev()
      });

      pswp.ui.registerElement({
        name: 'next-button',
        className: 'circle-button hover:animate-icon-inline',
        ariaLabel: window.themeVariables.strings.next,
        order: 3,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
            <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
          </svg>
        `,
        onClick: () => pswp.next()
      });
    }
  }
}

if (!window.customElements.get('dbd-lightbox')) {
  window.customElements.define('dbd-lightbox', DbdLightbox);
}
